import type {createPolicyControlPlane, PolicyPermission} from '../../../packages/admin/policy-publish.ts';
import {buildComboPolicyRevision} from '../../../packages/admin/combo-policy-control.ts';
import type {ComboPricingRule, BusinessPolicySnapshot} from '../../../packages/policy/business-policy.ts';

type PolicyControlPlane = ReturnType<typeof createPolicyControlPlane>;

export interface AdminComboPortConfig {
  readonly controlPlane: PolicyControlPlane;
  readonly actorId: string;
  readonly permissions: readonly PolicyPermission[];
  readonly now: () => string;
  readonly nextId: (kind: 'draft' | 'idempotency') => string;
}

export interface AdminComboSnapshot {
  readonly activePolicyVersion: string | null;
  readonly activeRules: readonly ComboPricingRule[];
  readonly draftId: string | null;
  readonly draftPolicyVersion: string | null;
  readonly validation: 'none' | 'valid' | 'invalid';
  readonly validationIssues: readonly string[];
  readonly publishedPolicyVersion: string | null;
}

const policyPayload = (value: unknown): BusinessPolicySnapshot => value as BusinessPolicySnapshot;

export function createAdminComboPort(config: AdminComboPortConfig) {
  const listeners = new Set<() => void>();
  let draftId: string | null = null;
  let draftPolicyVersion: string | null = null;
  let validation: AdminComboSnapshot['validation'] = 'none';
  let validationIssues: readonly string[] = [];
  let publishedPolicyVersion: string | null = null;

  const context = () => ({actorId: config.actorId, permissions: config.permissions, at: config.now()});
  const activePolicy = (): BusinessPolicySnapshot | null => {
    const active = config.controlPlane.active();
    return active ? policyPayload(active.payload) : null;
  };
  const buildSnapshot = (): AdminComboSnapshot => ({
    activePolicyVersion: config.controlPlane.active()?.policyVersion ?? null,
    activeRules: structuredClone(activePolicy()?.comboRules ?? []),
    draftId,
    draftPolicyVersion,
    validation,
    validationIssues,
    publishedPolicyVersion,
  });
  let snapshot = buildSnapshot();
  const publishSnapshot = () => {snapshot = buildSnapshot(); for (const listener of listeners) listener();};

  const createDraft = (input: {readonly policyVersion: string; readonly pricingRevision: string; readonly rulesRevision: string; readonly comboRules: readonly ComboPricingRule[]}) => {
    const active = activePolicy();
    if (!active) throw new Error('ADMIN_ACTIVE_POLICY_REQUIRED');
    const payload = buildComboPolicyRevision(active, input);
    const nextDraftId = config.nextId('draft');
    const created = config.controlPlane.createDraft({draftId: nextDraftId, payload}, context());
    draftId = created.draftId;
    draftPolicyVersion = input.policyVersion;
    validation = 'none'; validationIssues = []; publishedPolicyVersion = null;
    publishSnapshot();
    return created;
  };

  const validateDraft = () => {
    if (!draftId) throw new Error('ADMIN_COMBO_DRAFT_REQUIRED');
    const result = config.controlPlane.validateDraft(draftId, context());
    validation = result.ok ? 'valid' : 'invalid';
    validationIssues = [...result.issues];
    publishSnapshot();
    return result;
  };

  const publishDraft = () => {
    if (!draftId || !draftPolicyVersion) throw new Error('ADMIN_COMBO_DRAFT_REQUIRED');
    if (validation !== 'valid') throw new Error('ADMIN_COMBO_DRAFT_NOT_VALIDATED');
    const result = config.controlPlane.publish(draftId, {
      policyVersion: draftPolicyVersion,
      expectedActivePolicyVersion: config.controlPlane.active()?.policyVersion ?? null,
      idempotencyKey: config.nextId('idempotency'),
    }, context());
    publishedPolicyVersion = result.policyVersion;
    publishSnapshot();
    return result;
  };

  const activatePublished = () => {
    if (!publishedPolicyVersion) throw new Error('ADMIN_COMBO_PUBLISHED_REQUIRED');
    const result = config.controlPlane.activate({
      policyVersion: publishedPolicyVersion,
      expectedActivePolicyVersion: config.controlPlane.active()?.policyVersion ?? null,
      idempotencyKey: config.nextId('idempotency'),
    }, context());
    draftId = null; draftPolicyVersion = null; validation = 'none'; validationIssues = []; publishedPolicyVersion = null;
    publishSnapshot();
    return result;
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {listeners.add(listener); return () => listeners.delete(listener);},
    refresh() {publishSnapshot(); return snapshot;},
    createDraft,
    validateDraft,
    publishDraft,
    activatePublished,
  };
}

export type AdminComboPort = ReturnType<typeof createAdminComboPort>;
